import React, { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import styles from "./Style.module.css";
import PhoneNumberInput from "../../Components/FormInputs/PhoneNumberInput/PhoneNumberInput";
import { Header } from "../../Components/Header/Header";
import Timer from "../../Components/Timer/Timer";
import { SessionExpiredPopup } from "../../Components/Popup/SessionExpiredPopup/SessionExpiredPopup";
import { VerificationMethodPanel } from "../../Components/VerificationMethodPanel/VerificationMethodPanel";
import { useCustomer } from "../../Context/Customer/CustomerContext";
import { useSticky, buildPaxesParam, formatToSlotLabel } from "../../Utils/Helper";
import { useHoldExpiry } from "../../Utils/Hooks/useHoldExpiry";
import { useValidateBookingSession } from "../../Utils/Hooks/useValidateBookingSession";
import {
  getMobileVerificationError,
  OTP_RESEND_COOLDOWN_SECONDS,
} from "../../Utils/bookingVerification";
import { getCountryIdByIsoCode } from "../../Utils/countries";
import {
  getSessionStorageValue,
  setSessionStorageValue,
  STORAGE_KEYS,
} from "../../Utils/SessionStorage";
import { useGetCountries, useSendSMS, useGetCustomerDetails, useCheckSlotAvailable } from "../../APICalling/useApiQueries";

import { VerificationLayout } from "../../Components/VerificationLayout/VerificationLayout";
import { navigateWithInputFocus } from "../../Utils/navigateWithInputFocus";

import { SlotUnavailablePopup } from "../../Components/Popup/SlotUnavailablePopup/SlotUnavailablePopup";
import { BlockedCustomerPopup } from "../../Components/Popup/BlockedCustomerPopup/BlockedCustomerPopup";
import { isBlockedCustomerResponse } from "../../Utils/blockedCustomer";
import { useReleaseBookingHold } from "../../Utils/Hooks/useReleaseBookingHold";
import { useOtpApiAttempts } from "../../Utils/Hooks/useOtpApiAttempts";
import { getOtpSession, clearOtpSession } from "../../Utils/otpSession";
import { useAppointment } from "../../Context/Appointment/AppointmentContext";
import { useBookingTimer } from "../../Context/Timer/useBookingTimer";
import { useBookingSession } from "../../Context/BookingSession/BookingSessionContext";
import { useCreateBookingRequest } from "../../Utils/Hooks/useCreateBookingRequest";
import { useSchedule } from "../../Context/Schedule/ScheduleContext";
import { useGuests } from "../../Context/Guests/GuestsContext";
import { useLocation as useLocationContext } from "../../Context/Location/LocationContext";

const OVERLAY_CLASSES = ["overlay", "overflow-hidden", "overlay-index"];

export const VerifyIdentity = () => {
  useValidateBookingSession();

  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const headerRef = useRef(null);
  const { isSticky, sentinelRef } = useSticky({ scrollContainerRef, headerRef });
  const { isTimesup, handleSessionExpiredClose } = useHoldExpiry();
  const { formData, setFormData, setIsUserVerified, setVerifiedMobile } =
    useCustomer();

  const mobileErrorId = useId();
  const mobileInputWrapperRef = useRef(null);

  const [mobile, setMobile] = useState(typeof formData.mobile === "string" ? formData.mobile : "");
  const [countryCode, setCountryCode] = useState(
    () => parsePhoneNumberFromString(typeof formData.mobile === "string" ? formData.mobile : "")?.country || "SG"
  );
  const [mobileError, setMobileError] = useState("");
  const [callingCodeError, setCallingCodeError] = useState("");
  const { data: countryDirectory, isPending: countriesLoading } = useGetCountries();

  const { setMembership, setActualMembership } = useAppointment();
  const { resetTimer, startTimer } = useBookingTimer();
  const { uuid, setBookingID, setUuid } = useBookingSession();
  const { selectedDate, selectedTime } = useSchedule();
  const { members } = useGuests();
  const { selectedOutletID } = useLocationContext();
  const checkSlotAvailableMutation = useCheckSlotAvailable();

  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [slotUnavailableMessage, setSlotUnavailableMessage] = useState("");
  const [isCustomerBlocked, setIsCustomerBlocked] = useState(false);
  const { releaseHold } = useReleaseBookingHold();
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const mountedRef = useRef(true);
  const expiredRef = useRef(isTimesup);
  expiredRef.current = isTimesup;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const canContinueRequest = () => {
    const deadline = Number(getSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME));
    return mountedRef.current && !expiredRef.current && !(deadline > 0 && Date.now() >= deadline);
  };

  const { createBooking } = useCreateBookingRequest({
    onSlotUnavailable: (message) => {
      setIsCreatingBooking(false);
      setSlotUnavailableMessage(message);
    },
    onSuccess: (response) => {
      if (!canContinueRequest()) return;
      const result = typeof response?.success === "boolean" ? response : response?.data;
      if (result?.success) {
        const bookingId = result?.data?.booking?.id || null;
        const bookingUuid = result?.data?.booking?.uuid || null;
        setBookingID(bookingId);
        if (bookingUuid) setUuid(bookingUuid);
        if (bookingId && bookingUuid) startTimer();
      }
      setIsCreatingBooking(false);
      navigate("/contact-details", { replace: true });
    },
    onError: () => {
      if (!canContinueRequest()) return;
      setIsCreatingBooking(false);
      navigate("/contact-details", { replace: true });
    },
  });

  const sendAttempts = useOtpApiAttempts(mobile, "send");
  const sendPendingRef = useRef(false);
  const sessionCheckPendingRef = useRef(false);
  const otpSessionExpiredRef = useRef(false);
  const customerDetails = useGetCustomerDetails();

  // Blocked customers cannot book online at all — release the held slot and
  // show the Contact Our Team popup instead of an inline error.
  const handleBlockedCustomer = () => {
    setIsCustomerBlocked(true);
    setMobileError("");
    resetTimer();
    releaseHold();
  };

  const sendSMS = useSendSMS({
    onSuccess: (response, variables) => {
      const locked = sendAttempts.recordResponse(response, variables.mobile_number);
      if (!canContinueRequest()) return;
      if (isBlockedCustomerResponse(response)) {
        handleBlockedCustomer();
        return;
      }
      if (!response || response.success === false) {
        setMobileError(locked
          ? "" : response?.message || "Unable to send OTP. Please try again.");
        return;
      }
      const challenge = {
        mobile: variables.mobile_number,
        countryCode: parsePhoneNumberFromString(variables.mobile_number)?.country || countryCode,
        deliveryMethod: "sms",
        resendAvailableAt: Date.now() + OTP_RESEND_COOLDOWN_SECONDS * 1000,
      };
      challenge.countryId = getCountryIdByIsoCode(challenge.countryCode, countryDirectory?.byCode);
      setFormData((previous) => ({
        ...previous,
        mobile: challenge.mobile,
        countryId: challenge.countryId,
      }));
      setSessionStorageValue(STORAGE_KEYS.OTP_CHALLENGE, challenge);
      // Only an expired customer session invalidates the saved booking UUID.
      if (otpSessionExpiredRef.current) setUuid(null);
      clearOtpSession();
      navigateWithInputFocus(navigate, "/verify-otp", "booking-otp-digit-0");
    },
    onError: (error, variables) => {
      const locked = sendAttempts.recordResponse(error, variables.mobile_number);
      if (!canContinueRequest()) return;
      if (isBlockedCustomerResponse(error)) {
        handleBlockedCustomer();
        return;
      }
      const errorMsg = error?.response?.data?.message || "Failed to send OTP. Please try again.";
      setMobileError(locked ? "" : errorMsg);
    },
    onSettled: () => { sendPendingRef.current = false; },
  });

  const isRequestPending = isCheckingSession || sendSMS.isPending || customerDetails.isPending || isCreatingBooking;
  const deliveryActionsDisabled = isTimesup || isCustomerBlocked || Boolean(callingCodeError) || Boolean(getMobileVerificationError(mobile)) || isRequestPending || sendAttempts.isLocked;
  const displayedMobileError = sendAttempts.message || callingCodeError || mobileError;

  useEffect(() => {
    setIsUserVerified(false);
    setVerifiedMobile("");
  }, [setIsUserVerified, setVerifiedMobile]);

  useEffect(() => {
    if (isTimesup || slotUnavailableMessage || isCustomerBlocked) {
      document.body.classList.add(...OVERLAY_CLASSES);
    } else {
      document.body.classList.remove(...OVERLAY_CLASSES);
    }

    return () => document.body.classList.remove(...OVERLAY_CLASSES);
  }, [isTimesup, slotUnavailableMessage, isCustomerBlocked]);

  useEffect(() => {
    if (isTimesup) return;
    const frame = requestAnimationFrame(() => {
      mobileInputWrapperRef.current?.querySelector('#booking-otp-mobile')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [isTimesup]);

  const focusMobileInput = () => {
    mobileInputWrapperRef.current?.querySelector('#booking-otp-mobile')?.focus({ preventScroll: true });
  };

  const handleMobileChange = (value) => {
    setMobile(value || "");
    setMobileError(getMobileVerificationError(value || ""));
  };

  const requestOtp = async () => {
    if (!canContinueRequest() || callingCodeError || slotUnavailableMessage || isCustomerBlocked || isRequestPending || sendPendingRef.current || sessionCheckPendingRef.current || sendAttempts.isLockedNow()) return;

    const validationError = getMobileVerificationError(mobile);
    setMobileError(validationError);

    if (validationError) {
      focusMobileInput();
      return;
    }

    const mobileCountry = parsePhoneNumberFromString(mobile)?.country || countryCode;
    if (!getCountryIdByIsoCode(mobileCountry, countryDirectory?.byCode)) {
      setMobileError(countriesLoading
        ? "Country information is loading. Please try again in a moment."
        : "Please open the country list to retry or select an available country.");
      return;
    }

    // Validate existing session server-side before sending a new OTP.
    otpSessionExpiredRef.current = false;
    const storedSessionId = getOtpSession(mobile);
    if (storedSessionId) {
      sessionCheckPendingRef.current = true;
      setIsCheckingSession(true);
      try {
        const response = await customerDetails.mutateAsync({
          mobile_number: mobile,
          session_id: storedSessionId,
        });
        if (!canContinueRequest()) return;
        const data = response?.data?.data ?? response?.data;
        if (data?.verified === true) {
          // Check slot availability one more time before proceeding
          if (selectedDate && selectedTime && selectedOutletID) {
            try {
              const slotCheck = await checkSlotAvailableMutation.mutateAsync({
                panel: "customer",
                outlet: selectedOutletID,
                start_date: selectedDate,
                end_date: selectedDate,
                slot: formatToSlotLabel(selectedTime),
                paxes: buildPaxesParam(members),
                ...(uuid && { uuid }),
              });
              if (!canContinueRequest()) return;
              if (slotCheck?.success === false || slotCheck?.data?.available === false) {
                setSlotUnavailableMessage(slotCheck?.message || "This slot is no longer available. Please choose another slot/date.");
                return;
              }
            } catch (err) {
              const errData = err?.response?.data;
              if (errData?.slot_not_available || errData?.data?.available === false) {
                setSlotUnavailableMessage(errData?.message || "This slot is no longer available. Please choose another slot/date.");
                return;
              }
              // Non-blocking for other errors: proceed with booking
            }
          }

          const skipCountryId = getCountryIdByIsoCode(mobileCountry, countryDirectory?.byCode);
          if (!canContinueRequest()) return;
          const user = data?.user ?? data;
          setFormData((previous) => ({
            ...previous,
            mobile,
            ...(skipCountryId ? { countryId: skipCountryId } : {}),
            ...(user?.first_name != null ? { fname: user.first_name } : {}),
            ...(user?.last_name != null ? { lname: user.last_name } : {}),
            ...(user?.email != null ? { email: user.email } : {}),
            ...(Object.prototype.hasOwnProperty.call(user, "notes")
              ? { notes: user.notes ?? "" } : {}),
          }));
          setMembership(true);
          setActualMembership(data?.is_membership);
          resetTimer();
          setVerifiedMobile(mobile);
          setIsUserVerified(true);
          setIsCreatingBooking(true);
          createBooking({ mobile_number: mobile, actualMembership: data?.is_membership, customerFirstName: user?.first_name });
          return;
        }
        otpSessionExpiredRef.current = data?.verified === false;
      } catch (error) {
        const status = error?.response?.status;
        const responseData = error?.response?.data;
        const data = responseData?.data ?? responseData;
        otpSessionExpiredRef.current = [401, 403, 419].includes(status) || data?.verified === false;
      } finally {
        sessionCheckPendingRef.current = false;
        if (mountedRef.current) setIsCheckingSession(false);
      }
    }

    if (!canContinueRequest()) return;

    // Keep the keyboard open during the request so focus can transfer to the
    // OTP field after navigation, including on browsers requiring a user gesture.
    focusMobileInput();
    sendPendingRef.current = true;
    sendSMS.mutate({ mobile_number: mobile });
  };

  return (
    <>
      <VerificationLayout
        className={styles.verifyIdentityPage}
        scrollContainerRef={scrollContainerRef}
        footer={
          <VerificationMethodPanel
            title={sendAttempts.isLocked ? "Try again" : sendSMS.isPending ? "Sending OTP…" : "Send OTP"}
            resendCountdown={sendAttempts.secondsRemaining}
            disabled={deliveryActionsDisabled}
            onSelect={requestOtp}
          />
        }
      >
        <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
        <div ref={headerRef} className={`${styles.header} sticky-header ${isSticky ? "is-sticky" : ""}`}>
          <Timer customClass={styles.timer} />
          <Header
            title="Verify Your Mobile Number"
            description="We'll send you a one-time password (OTP) to verify your mobile number."
            onBack={() => navigate("/review-and-confirm-booking")}
          />
        </div>

        <div className={styles.pageContent}>
          <main className={styles.deliveryContent}>
            <div className={styles.mobileField} ref={mobileInputWrapperRef}>
              <label htmlFor="booking-otp-mobile">
                Mobile Number<span aria-hidden="true">*</span>
              </label>
              <PhoneNumberInput
                id="booking-otp-mobile"
                name="bookingOtpMobile"
                placeholder="Enter Mobile Number"
                defaultCountry="SG"
                international={false}
                value={mobile}
                onChange={handleMobileChange}
                onCallingCodeValidation={setCallingCodeError}
                // editableCallingCode
                onBlur={() => setMobileError(getMobileVerificationError(mobile))}
                onCountryChange={(country) => {
                  setCountryCode(country || "");
                }}
                className={`form-control ${displayedMobileError ? "is-invalid" : ""}`}
                aria-invalid={Boolean(displayedMobileError)}
                aria-describedby={displayedMobileError ? mobileErrorId : undefined}
                aria-required="true"
                disabled={isTimesup}
                readOnly={isRequestPending}
                autoComplete="tel"
                inputMode="tel"
                enterKeyHint="send"
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    requestOtp();
                  }
                }}
              />
              {displayedMobileError ? (
                <p id={mobileErrorId} className={styles.fieldError} role="alert">
                  {displayedMobileError}
                </p>
              ) : null}
              {!sendAttempts.isLocked && sendAttempts.attemptsLeft !== null && (
                <p className={styles.fieldError}>
                  SMS attempts remaining: {sendAttempts.attemptsLeft}
                  {sendAttempts.maxAttempts !== null ? ` of ${sendAttempts.maxAttempts}` : ""}
                </p>
              )}
            </div>
          </main>
        </div>
      </VerificationLayout>

      {isTimesup ? (
        <SessionExpiredPopup
          active={isTimesup}
          onClose={handleSessionExpiredClose}
        />
      ) : null}
      <SlotUnavailablePopup
        active={!!slotUnavailableMessage}
        message={slotUnavailableMessage}
        onClose={() => setSlotUnavailableMessage("")}
      />
      <BlockedCustomerPopup
        active={isCustomerBlocked}
        onReturnHome={() => navigate("/", { replace: true })}
        onClose={() => setIsCustomerBlocked(false)}
      />
    </>
  );
};

export default VerifyIdentity;
